// Application bootstrap - initialization and viewer setup

import { auth } from '@/api'
import { debug } from '@/utils/debug'
import { isDemoMode } from '@/utils/demoMode'
import { initLocale, t, translatePage } from '@/i18n'
import { VERSION, GIT_HASH } from '@/version'
import {
  initGA4,
  trackSessionStart,
  trackGardenLoaded,
  trackGardenError,
  trackCameraModeChanged,
  trackWalkModeToggled,
  trackLayerVisibilityChanged
} from '@/analytics'
import { GardenViewer } from './GardenViewer'
import {
  // State
  getViewer,
  setViewer,
  isViewerStarting,
  setViewerStarting,
  setLeftPanel,
  // Elements
  canvasContainer,
  loginBtn,
  viewToggle,
  walkToggle,
  layerPlantsBtn,
  layerBuildingsBtn,
  layerGroundBtn,
  layerMetadataBtn,
  loadingStatus,
  toolHandBtn,
  toolPointerBtn,
  demoBadge,
  bottomBarInfo,
  bottomBarInfoSpacer,
  // UI functions
  showLoading,
  hideLoading,
  showError,
  showAuth,
  hideAuth,
  showUI,
  updateUI,
  updateProgress,
  resetProgressLookup,
  updateWalkToggle,
  toggleViewMode,
  toggleWalkMode,
  toggleLayerVisibility,
  selectToolMode,
  updateBottomBarHint,
  handleGlobalKeydown,
  showGardenSelector,
  // Top bar
  initTopBar,
  updateViewModeButtons,
  updateTopBarLayerToggles,
  setupTopBarListeners,
  // Left panel
  LeftPanel,
  MapTabContent,
  MetadataTabContent,
  GroundTabContent,
  ArchitectureTabContent,
  PlantsTabContent,
  leftPanelContainer,
  // Floating properties panel
  FloatingPropsPanel,
  // Placement panel
  PlacementPanel,
  stopContinuousPlacement,
  // Demo panel
  DemoPanel,
  // Canvas controls
  initCanvasControls,
  setCanvasControlsVisible
} from '@/ui'
import { isEditMode } from '@/utils/editMode'
import { onMcpBoundaryImported } from '@/zones'
import { saveService } from '@/api/SaveService'
import { getBoundaryBounds, calculateAlignmentAngle } from '@/geodata/boundaryImport'
import { getGeoAnchor } from '@/geodata/geoAnchorState'
import { getParcel } from '@/geodata/parcelState'
import { showAlignmentPrompt } from '@/ui/AlignmentPrompt'
import { undoStack } from '@/editor/UndoStack'
import type { LayerTab } from '@/ui/LeftPanel'

/**
 * Map left panel tab to selection layer name.
 * Returns a layer that matches objects (plants/buildings/ground/metadata)
 * or a name that matches nothing (effectively locking all layers).
 */
function tabToLayer(tab: LayerTab): string {
  switch (tab) {
    case 'plants': return 'plants'
    case 'architecture': return 'buildings'
    case 'ground': return 'ground'
    case 'metadata': return 'metadata'
    case 'map': return 'map' // No objects belong to 'map' → all locked
    case 'favorites': return 'plants' // Favorites shows plants
    default: return 'unknown'
  }
}

/**
 * Get garden ID from URL
 */
function getGardenIdFromUrl(): string | null {
  // Support formats: /gardens/{id} or ?garden={id}
  const pathMatch = window.location.pathname.match(/\/gardens\/([^/]+)/)
  if (pathMatch) {
    return pathMatch[1]
  }

  const params = new URLSearchParams(window.location.search)
  return params.get('garden')
}

/**
 * Get initial camera mode from URL
 */
function getCameraFromUrl(): '2d' | '3d' {
  const params = new URLSearchParams(window.location.search)
  const camera = params.get('camera')?.toLowerCase()
  // Default to 2D, only switch to 3D if explicitly requested
  return camera === '3d' ? '3d' : '2d'
}

/** Auto-run walkthrough on first visit (lazy-loaded) */
function autoStartWalkthroughIfNew(): void {
  import('./ui/Walkthrough').then(({ autoStartWalkthroughIfNew: auto }) => auto())
}

/**
 * Start the garden viewer
 * @param demoMode - If true, load from static demo files instead of API
 */
async function startViewer(demoMode: boolean = false): Promise<void> {
  // Prevent concurrent starts
  if (isViewerStarting()) {
    debug.log('Viewer', 'Viewer already starting, skipping...')
    return
  }
  if (getViewer()) {
    debug.log('Viewer', 'Viewer already exists, skipping...')
    return
  }

  setViewerStarting(true)
  debug.log('Viewer', `Starting viewer...${demoMode ? ' (demo mode)' : ''}`)
  showLoading(demoMode ? t('demo.loading') : t('status.initializingViewer'))

  // Hoist mapTabContent so onGardenLoaded callback can sync state after load
  let mapTabContent: InstanceType<typeof MapTabContent> | null = null

  // Create viewer
  const viewer = new GardenViewer({
    container: canvasContainer,
    demoMode,
    onStatusChange: (status) => {
      loadingStatus.textContent = status
      updateProgress(status)
    },
    onGardenLoaded: (garden, metrics) => {
      hideLoading()
      showUI()
      updateUI(garden)

      // Show demo badge if in demo mode
      if (demoMode) {
        demoBadge.style.display = 'flex'
        ;(demoBadge as HTMLAnchorElement).href = t('demo.purchaseUrl')
      }

      // After duplicate: save immediately to generate preview thumbnail for the new copy
      const loadParams = new URLSearchParams(window.location.search)
      if (loadParams.get('duplicated') === 'true') {
        // Clean the flag from URL
        const cleanUrl = new URL(window.location.href)
        cleanUrl.searchParams.delete('duplicated')
        window.history.replaceState({}, '', cleanUrl.toString())
        // Trigger save (async, fire-and-forget) to generate thumbnail
        debug.log('App', 'Post-duplicate: saving to generate preview thumbnail')
        saveService.save(garden.id, viewer.getSchemeOutline(), garden)
      }

      // Sync map tab content with restored parcel + background state
      if (mapTabContent) {
        const restoredParcel = getParcel()
        if (restoredParcel) {
          mapTabContent.setParcel({
            id: restoredParcel.id,
            area: restoredParcel.area,
            region: restoredParcel.region,
            address: restoredParcel.address,
            boundary: [],
            boundaryPl2000: [],
          })
        }
        if (viewer.hasMapBackground()) {
          mapTabContent.setHasMapBackground(true)
        }
      }

      // Track garden loaded analytics event
      const userId = auth.getState().userId
      trackGardenLoaded({
        gardenId: metrics.gardenId,
        objectCount: metrics.objectCount,
        loadTimeMs: metrics.loadTimeMs,
        userId,
        demoMode,
        editMode: isEditMode(),
        debugMode: debug.isEnabled()
      })
    },
    onError: (error) => {
      showError(error)
      // Track garden error analytics event
      trackGardenError('load_failed', undefined, error.message)
    }
  })

  setViewer(viewer)

  // Initialize and wait for it to complete
  debug.log('Viewer', 'Initializing viewer...')
  try {
    await viewer.initialize()
    debug.success('Viewer', 'Viewer initialized')

    // Set initial view mode from URL parameter (default: 2D)
    const initialCamera = getCameraFromUrl()
    viewer.setViewMode(initialCamera)
    debug.log('Viewer', `Initial view mode: ${initialCamera}`)

    // Initialize top bar now that viewer is ready
    initTopBar()
    updateViewModeButtons()
    updateTopBarLayerToggles()

    // Initialize left panel (edit mode only)
    if (isEditMode()) {
      leftPanelContainer.style.display = 'flex'
      const leftPanel = new LeftPanel(leftPanelContainer)
      setLeftPanel(leftPanel)

      // Register map tab content
      mapTabContent = new MapTabContent()
      const mtc = mapTabContent // local const for closure type narrowing
      mtc.onBoundaryImported = (parcel, schemeOutline) => {
        debug.log('Map', `Boundary imported: ${parcel.id}`, schemeOutline)
        // Reset garden rotation — new boundary starts north-up.
        // Alignment prompt may set it later.
        viewer.setGardenRotation(0)
        // Clear stale map background state (new boundary = new position)
        mtc.setHasMapBackground(false)
        // Render the boundary outline in the 3D scene
        viewer.setSchemeOutline(schemeOutline)
        // Show geo-anchor pin marker
        viewer.renderGeoAnchor(getGeoAnchor())
        // Mark geodata as dirty (user action → needs save)
        saveService.markGeodataDirty()
        // Center & zoom on imported boundary (G11)
        focusOnBoundary(viewer, schemeOutline.points)
        // G12: Show alignment prompt if needed
        showAlignmentPromptIfNeeded(viewer, schemeOutline.points)
      }
      mtc.onBoundaryCleared = () => {
        debug.log('Map', 'Boundary cleared')
        // Clear the boundary outline and geo-anchor marker from the scene
        viewer.clearSchemeOutline()
        viewer.renderGeoAnchor(null)
        // Mark geodata as dirty (user action → needs save)
        saveService.markGeodataDirty()
      }

      // G13: Wire "Get map background" button
      mtc.onGetMapBackground = () => handleGetMapBackground(viewer, mtc)

      // G13: Wire "Clear map background" button
      mtc.onClearMapBackground = () => handleClearMapBackground(viewer, mtc)

      // G14: Wire "Import buildings" button
      mtc.onImportBuildings = () => handleImportBuildings(viewer, mtc)

      leftPanel.registerTabContent('map', mtc)

      // Register metadata tab content
      const metadataTabContent = new MetadataTabContent()
      leftPanel.registerTabContent('metadata', metadataTabContent)

      // Register ground tab content
      const groundTabContent = new GroundTabContent()
      leftPanel.registerTabContent('ground', groundTabContent)

      // Register architecture tab content
      const architectureTabContent = new ArchitectureTabContent()
      leftPanel.registerTabContent('architecture', architectureTabContent)

      // Register plants tab content
      const plantsTabContent = new PlantsTabContent()
      leftPanel.registerTabContent('plants', plantsTabContent)

      // Initialize floating properties panel
      const scene = viewer.getScene()
      if (scene) {
        new FloatingPropsPanel(document.body, scene)
        debug.log('App', 'Floating properties panel initialized')
      }

      // Wire tab switching to layer lock
      leftPanel.onTabSelect = (tab) => {
        viewer.setActiveLayer(tabToLayer(tab))
      }
      // Initial state: all layers locked until user clicks a tab
      viewer.setActiveLayer('none')

      // Initialize placement panel
      const placementPanel = new PlacementPanel(document.body)
      leftPanel.onExpandedChange = (expanded) => {
        placementPanel.setLeftPanelExpanded(expanded)
      }
      debug.log('App', 'Placement panel initialized')

      // Wire MCP boundary import to viewer + map tab UI
      onMcpBoundaryImported((parcel, schemeOutline) => {
        debug.log('Map', `MCP boundary imported: ${parcel.id}`, schemeOutline)
        viewer.setGardenRotation(0)
        mtc.setHasMapBackground(false)
        viewer.setSchemeOutline(schemeOutline)
        viewer.renderGeoAnchor(getGeoAnchor())
        mtc.setParcel(parcel)
        saveService.markGeodataDirty()
        // Center & zoom on imported boundary (G11)
        focusOnBoundary(viewer, schemeOutline.points)
        // G12: Show alignment prompt if needed
        showAlignmentPromptIfNeeded(viewer, schemeOutline.points)
      })
    }

    // Initialize demo panel (demo mode only, replaces left panel)
    if (demoMode) {
      leftPanelContainer.style.display = 'flex'
      new DemoPanel(leftPanelContainer)
      debug.log('App', 'Demo panel initialized')
    }

    // Initialize canvas controls (compass + zoom)
    initCanvasControls()

    // Listen for walk mode changes (e.g., when user presses Escape)
    viewer.setOnWalkModeChange((enabled) => {
      updateWalkToggle()
      updateViewModeButtons() // Update top bar too
      setCanvasControlsVisible(!enabled) // Hide controls in walk mode
      viewer.deselectAll()
      stopContinuousPlacement()
      trackWalkModeToggled(enabled)
    })

    // Listen for view mode changes
    viewer.setOnViewModeChange((mode) => {
      updateViewModeButtons() // Update top bar too
      viewer.deselectAll()
      stopContinuousPlacement()
      trackCameraModeChanged(mode)
    })

    // Listen for layer visibility changes
    viewer.setOnLayerVisibilityChange((layer, visible) => {
      updateTopBarLayerToggles() // Update top bar too
      trackLayerVisibilityChanged(layer, visible)
    })
  } catch (error) {
    debug.error('Viewer', 'Failed to initialize', error)
    setViewerStarting(false)
    setViewer(null)
    throw error
  }

  // Viewer is ready, allow future calls (they'll be blocked by viewer !== null check)
  setViewerStarting(false)

  // Wire up preview capture callback (generates thumbnail on every save)
  saveService.onCapturePreview = () => viewer.capturePreview()

  // Wire up deferred garden creation callback (updates URL after first save)
  saveService.onGardenCreated = (gardenId) => {
    const url = new URL(window.location.href)
    url.searchParams.delete('new')
    url.searchParams.set('garden', gardenId)
    window.history.replaceState({}, '', url.toString())
    debug.log('App', `URL updated after garden creation: ${url.toString()}`)
  }

  // In demo mode, load demo garden directly
  if (demoMode) {
    debug.log('Garden', 'Demo mode: loading demo garden')
    await viewer.loadGarden('demo')
    autoStartWalkthroughIfNew()
    return
  }

  // Check for new garden flow: ?new=true
  const params = new URLSearchParams(window.location.search)
  if (params.get('new') === 'true') {
    debug.log('Garden', 'New garden mode: creating empty garden')
    await viewer.createEmptyGarden(t('ui.defaultGardenName'))
    autoStartWalkthroughIfNew()
    return
  }

  // Load garden from URL or use first available
  const gardenId = getGardenIdFromUrl()
  debug.log('Garden', `Garden ID from URL: ${gardenId || 'none'}`)

  if (gardenId) {
    await viewer.loadGarden(gardenId)
    autoStartWalkthroughIfNew()
  } else {
    // No garden ID specified - show garden selector
    try {
      debug.log('API', 'No garden ID specified, fetching garden list...')
      showLoading(t('selector.loading'))
      const { api } = await import('@/api')
      const gardens = await api.getGardens()

      debug.log('API', `Found ${gardens.length} gardens`, gardens)

      // Show garden selector (handles empty list case)
      showGardenSelector(gardens)
    } catch (error) {
      debug.error('API', 'Failed to load gardens list', error)
      showError(error)
    }
  }
}

/**
 * Set bottom bar copyright text and version info
 */
function initBottomBarInfo(): void {
  const year = new Date().getFullYear()
  const copyright = t('footer.copyright')
  const versionText = `\u00a9 ${year} ${copyright}  \u00b7  v${VERSION}`

  // Build info span with (i) button for What's New
  bottomBarInfo.innerHTML = ''
  bottomBarInfo.append(
    document.createTextNode(versionText + ' '),
  )
  const infoBtn = document.createElement('button')
  infoBtn.title = t('whatsNew.title', "What's New")
  infoBtn.innerHTML = '<i class="fa-solid fa-circle-info"></i>'
  Object.assign(infoBtn.style, {
    all: 'unset',
    color: 'rgba(255, 255, 255, 0.35)',
    cursor: 'pointer',
    fontSize: '11px',
    verticalAlign: 'middle',
    transition: 'color 0.2s',
  })
  infoBtn.addEventListener('mouseenter', () => { infoBtn.style.color = 'rgba(255, 255, 255, 0.7)' })
  infoBtn.addEventListener('mouseleave', () => { infoBtn.style.color = 'rgba(255, 255, 255, 0.35)' })
  infoBtn.addEventListener('click', async () => {
    const { WhatsNewModal } = await import('@/ui/WhatsNewModal')
    const modal = new WhatsNewModal()
    modal.open()
  })
  bottomBarInfo.appendChild(infoBtn)

  // Mirror text width to right spacer to keep hint centered
  bottomBarInfoSpacer.textContent = versionText
}

/**
 * Initialize the application
 */
export async function initApp(): Promise<void> {
  // Initialize i18n before anything else
  initLocale()
  translatePage()

  // Set bottom bar copyright + version
  initBottomBarInfo()

  // Initialize GA4 analytics
  initGA4()

  debug.log('App', 'Garden Viewer 3D initializing...')
  debug.log('App', `Version: ${VERSION} (${GIT_HASH})`)
  debug.log('App', `URL: ${window.location.href}`)

  // Check for OAuth callback
  const params = new URLSearchParams(window.location.search)
  if (params.has('code')) {
    debug.log('Auth', 'OAuth callback detected, exchanging code...')

    // Restore locale from saved params so loading message shows in correct language
    const savedJson = sessionStorage.getItem('oauth_return_params')
    if (savedJson) {
      try {
        const saved = JSON.parse(savedJson) as Record<string, string>
        if (saved.lang) {
          const url = new URL(window.location.href)
          url.searchParams.set('lang', saved.lang)
          window.history.replaceState({}, '', url.toString())
          initLocale()
          translatePage()
        }
      } catch { /* ignore */ }
    }

    hideAuth()
    showLoading(t('status.completingSignIn'))
    const success = await auth.handleCallback()
    if (!success) {
      debug.error('Auth', 'OAuth callback failed')
      hideLoading()
      showAuth()
      return
    }

    // URL params are now restored - reinitialize locale and debug
    initLocale()
    translatePage()
    debug.reinitialize()
    // Rebuild progress lookup with new locale
    resetProgressLookup()

    debug.success('Auth', 'OAuth callback successful')
  }

  // Check for demo mode first
  const inDemoMode = isDemoMode()
  if (inDemoMode) {
    debug.log('App', 'Demo mode detected, skipping auth check')
  }

  // Check existing session
  const authState = auth.getState()
  debug.log('Auth', 'Current auth state', authState)

  // Track session start with auth state
  trackSessionStart(authState.isAuthenticated)

  if (inDemoMode) {
    // Demo mode: skip auth, load demo garden
    debug.log('App', 'Starting demo viewer...')
    hideAuth()
    await startViewer(true)  // demoMode = true, loads demo garden automatically
  } else if (authState.isAuthenticated) {
    debug.success('Auth', 'User is authenticated')
    hideAuth()  // Hide auth panel immediately
    showLoading(t('status.loading'))
    await startViewer()
  } else {
    // Not authenticated
    if (isEditMode() || params.has('new')) {
      // User came from main app — skip sign-in screen, redirect to OAuth directly
      debug.log('Auth', 'User not authenticated but edit/new param present, auto-redirecting to OAuth')
      auth.startLogin()
      return
    }

    // Show sign-in screen for other cases
    debug.log('Auth', 'User not authenticated, showing sign-in screen')
    hideLoading()
    showAuth()

    // Set up login button before returning
    loginBtn.addEventListener('click', () => {
      debug.log('Auth', 'Login button clicked, starting OAuth flow...')
      auth.startLogin()
    })

    return  // Wait for user to sign in (page will reload after OAuth)
  }

  // Subscribe to auth changes
  auth.subscribe((state) => {
    debug.log('Auth', 'Auth state changed', state)
    if (state.isAuthenticated) {
      hideAuth()
      startViewer()
    } else {
      showAuth()
      const viewer = getViewer()
      if (viewer) {
        viewer.dispose()
        setViewer(null)
      }
    }
  })

  // Login button click
  loginBtn.addEventListener('click', () => {
    debug.log('Auth', 'Login button clicked, starting OAuth flow...')
    auth.startLogin()
  })

  // View toggle button click
  viewToggle.addEventListener('click', toggleViewMode)

  // Walk mode toggle button click
  walkToggle.addEventListener('click', toggleWalkMode)

  // Layer toggle button clicks (old UI - keep for backward compatibility)
  layerPlantsBtn.addEventListener('click', () => toggleLayerVisibility('plants'))
  layerBuildingsBtn.addEventListener('click', () => toggleLayerVisibility('buildings'))
  layerGroundBtn.addEventListener('click', () => toggleLayerVisibility('ground'))
  layerMetadataBtn.addEventListener('click', () => toggleLayerVisibility('metadata'))

  // Top bar event listeners (new UI)
  setupTopBarListeners()

  // Tool mode button clicks (edit mode only)
  toolHandBtn.addEventListener('click', () => selectToolMode('hand'))
  toolPointerBtn.addEventListener('click', () => selectToolMode('pointer'))

  // Ruler button (top bar) — wired here alongside other tool buttons
  document.getElementById('top-bar-ruler-btn')?.addEventListener('click', () => {
    selectToolMode('ruler')
  })

  // Update bottom bar hint when active tool changes
  getViewer()?.getToolManager()?.onToolChange(() => updateBottomBarHint())

  // Edit mode logging
  if (isEditMode()) {
    debug.log('App', 'Edit mode enabled')
  }

  // Keyboard shortcuts
  document.addEventListener('keydown', handleGlobalKeydown)
}

/**
 * G11: Center & zoom camera on imported boundary points.
 * Skips if boundary is degenerate (0 area).
 */
function focusOnBoundary(
  viewer: GardenViewer,
  points: Array<{ x: number; y: number }>
): void {
  if (!points || points.length < 3) return
  const bounds = getBoundaryBounds(points)
  if (bounds.width <= 0 || bounds.height <= 0) return
  const scene = viewer.getScene()
  if (!scene) return
  // Shrink bounds to 70% so the boundary fills more of the viewport
  // (focusOn already adds 20% padding internally)
  scene.focusOn(bounds.centerX, bounds.centerY, 0, bounds.width * 0.7, bounds.height * 0.7)
  debug.log('App', `Focused on boundary: center=(${bounds.centerX.toFixed(0)}, ${bounds.centerY.toFixed(0)}), size=${bounds.width.toFixed(0)}×${bounds.height.toFixed(0)}`)
}

/**
 * G12: Show alignment prompt if the boundary is not already aligned.
 * Only shows when the alignment angle exceeds 1 degree.
 */
async function showAlignmentPromptIfNeeded(
  viewer: GardenViewer,
  points: Array<{ x: number; y: number }>
): Promise<void> {
  if (!points || points.length < 3) return

  const angle = calculateAlignmentAngle(points)
  if (Math.abs(angle) <= 1) {
    debug.log('App', `Boundary already aligned (angle=${angle.toFixed(1)}°), skipping prompt`)
    return
  }

  debug.log('App', `Boundary alignment angle: ${angle.toFixed(1)}°, showing prompt`)
  const choice = await showAlignmentPrompt()

  if (choice === 'align') {
    // Snapshot for undo
    const outlineBefore = viewer.getSchemeOutline()
    const gardenBefore = viewer.getGarden()
    const rotationBefore = gardenBefore?.rotation ?? 0

    viewer.rotateBoundary(angle)

    // Undo support: revert both boundary points AND garden rotation
    const outlineAfter = viewer.getSchemeOutline()
    if (outlineBefore && outlineAfter) {
      const beforeSnapshot = { x: outlineBefore.x, y: outlineBefore.y, points: outlineBefore.points.map(p => ({ ...p })) }
      const afterSnapshot = { x: outlineAfter.x, y: outlineAfter.y, points: outlineAfter.points.map(p => ({ ...p })) }
      const rotationAfter = gardenBefore?.rotation ?? 0
      undoStack.push({
        description: 'Align boundary horizontally',
        undo: () => {
          viewer.setSchemeOutline(beforeSnapshot)
          const garden = viewer.getGarden()
          if (garden) {
            garden.rotation = rotationBefore
            viewer.getScene()?.setSunDirection(rotationBefore)
          }
        },
        redo: () => {
          viewer.setSchemeOutline(afterSnapshot)
          const garden = viewer.getGarden()
          if (garden) {
            garden.rotation = rotationAfter
            viewer.getScene()?.setSunDirection(rotationAfter)
          }
        },
      })
    }

    // Re-focus after rotation
    const newOutline = viewer.getSchemeOutline()
    if (newOutline) {
      // Get absolute points for focus calculation
      const absolutePoints = newOutline.points.map(p => ({
        x: newOutline.x + p.x,
        y: newOutline.y + p.y,
      }))
      focusOnBoundary(viewer, absolutePoints)
    }

    debug.log('App', `Boundary aligned by ${angle.toFixed(1)}°`)
  } else {
    debug.log('App', 'User chose to keep north at top')
  }
}

/**
 * G13: Handle "Get map background" button click.
 * Re-fetches parcel by ID to get PL-2000 boundary, downloads WMS image,
 * saves as background, and renders immediately.
 */
async function handleGetMapBackground(
  viewer: GardenViewer,
  mapTabContent: InstanceType<typeof MapTabContent>
): Promise<void> {
  const parcelMeta = getParcel()
  const anchor = getGeoAnchor()
  const garden = viewer.getGarden()
  if (!parcelMeta || !anchor || !garden) return

  mapTabContent.setMapBgState('loading')

  try {
    // Dynamically import services
    const { kiegService } = await import('@/geodata/KIEGService')
    const { coordinateService } = await import('@/geodata/CoordinateService')

    // Get PL-2000 boundary — use stored boundary if available (merged parcels), otherwise re-fetch
    let boundaryPl2000 = parcelMeta.boundaryPl2000
    if (!boundaryPl2000) {
      const { uldkService } = await import('@/geodata/ULDKService')
      const parcelResult = await uldkService.getParcelById(parcelMeta.id)
      if (!parcelResult.ok) {
        throw new Error(parcelResult.error.message)
      }
      boundaryPl2000 = parcelResult.value.boundaryPl2000
    }

    // Fetch WMS map image
    const wmsResult = await kiegService.fetchMapImage(boundaryPl2000)

    // Calculate SchemeImage position params
    // BBOX center in PL-2000 → editor coords
    const bboxCenterPl = {
      x: (wmsResult.bbox.minX + wmsResult.bbox.maxX) / 2,
      y: (wmsResult.bbox.minY + wmsResult.bbox.maxY) / 2,
    }
    const bboxCenterEditor = coordinateService.pl2000ToEditor(bboxCenterPl, anchor)

    const dotsPerMeter = anchor.dotsPerMeter
    const displayWidthDots = wmsResult.widthMeters * dotsPerMeter
    const displayHeightDots = wmsResult.heightMeters * dotsPerMeter
    const scale = displayWidthDots / wmsResult.widthPx

    // x, y = top-left corner in editor dots
    const x = bboxCenterEditor.x - displayWidthDots / 2
    const y = bboxCenterEditor.y - displayHeightDots / 2
    const rotation = garden.rotation || 0

    // Save to server (or defer for new gardens without server ID)
    const { saveService } = await import('@/api/SaveService')
    if (garden.id) {
      await saveService.saveBackgroundImage(garden.id, wmsResult.dataUri, x, y, scale, rotation)
    } else {
      saveService.markBackgroundDirty(wmsResult.dataUri, x, y, scale, rotation)
      debug.log('App', 'Background image deferred — garden not yet saved')
    }

    // Build SchemeImage and render immediately
    const schemeImage = {
      imageUrl: wmsResult.dataUri,
      x, y, scale, rotation,
    }
    await viewer.loadWmsBackground(schemeImage)

    mapTabContent.setHasMapBackground(true)
    mapTabContent.setMapBgState('success')
    debug.success('App', 'WMS map background loaded and saved')
  } catch (error) {
    debug.error('App', 'Failed to get map background', error)
    mapTabContent.setMapBgState('error')
  }
}

/**
 * G13: Handle "Clear map background" button click.
 */
async function handleClearMapBackground(
  viewer: GardenViewer,
  mapTabContent: InstanceType<typeof MapTabContent>
): Promise<void> {
  const garden = viewer.getGarden()
  if (!garden) return

  // Clear from scene
  viewer.clearMapBackground()
  mapTabContent.setHasMapBackground(false)

  // Clear on server (if garden has been saved), or clear pending state
  if (garden.id) {
    try {
      const { saveService } = await import('@/api/SaveService')
      await saveService.clearBackgroundImage(garden.id)
      debug.success('App', 'Map background cleared from server')
    } catch (error) {
      debug.warn('App', 'Failed to clear map background on server', error)
    }
  } else {
    const { saveService } = await import('@/api/SaveService')
    saveService.clearPendingBackground()
  }
}

/**
 * G14: Handle "Import buildings" button click.
 * Re-fetches parcel, grid-scans for buildings, imports as building zones.
 */
async function handleImportBuildings(
  viewer: GardenViewer,
  mapTabContent: InstanceType<typeof MapTabContent>
): Promise<void> {
  const parcelMeta = getParcel()
  const anchor = getGeoAnchor()
  const garden = viewer.getGarden()
  if (!parcelMeta || !anchor || !garden) return

  mapTabContent.setImportBuildingsState('loading', 0)

  try {
    const { scanForBuildings } = await import('@/geodata/buildingImport')
    const { zoneState } = await import('@/zones')

    // Get PL-2000 boundary — use stored boundary if available (merged parcels), otherwise re-fetch
    let boundaryPl2000 = parcelMeta.boundaryPl2000
    if (!boundaryPl2000) {
      const { uldkService } = await import('@/geodata/ULDKService')
      const parcelResult = await uldkService.getParcelById(parcelMeta.id)
      if (!parcelResult.ok) {
        throw new Error(parcelResult.error.message)
      }
      boundaryPl2000 = parcelResult.value.boundaryPl2000
    }

    // Snapshot zone state for undo
    const stateBefore = zoneState.exportState()

    // Scan for buildings
    const buildings = await scanForBuildings(
      boundaryPl2000,
      anchor,
      garden.rotation || 0,
      (progress) => mapTabContent.setImportBuildingsState('loading', Math.round(progress))
    )

    if (buildings.length === 0) {
      mapTabContent.setImportBuildingsState('success', 0)
      return
    }

    // Import buildings as building zones
    const addedIds: string[] = []
    for (const building of buildings) {
      const id = zoneState.addZone(
        'building',
        building.centerX, building.centerY,
        building.width, building.height,
        { points: building.editorPoints, label: building.functionLabel }
      )
      addedIds.push(id)
    }

    // Snapshot after for undo
    const stateAfter = zoneState.exportState()

    // Push single undo command for the whole batch
    undoStack.push({
      description: `Import ${buildings.length} buildings`,
      undo: () => zoneState.importState(stateBefore),
      redo: () => zoneState.importState(stateAfter),
    })

    // Mark as dirty for save
    saveService.markGeodataDirty()

    mapTabContent.setImportBuildingsState('success', buildings.length)
    debug.success('App', `Imported ${buildings.length} buildings as zones`)
  } catch (error) {
    debug.error('App', 'Failed to import buildings', error)
    mapTabContent.setImportBuildingsState('error')
  }
}
